import React, { useState } from 'react'
import Radom, { Deposit, DepositAccount } from '../../state/Radom'
import Table from '../../components/Table'
import { RADOM_COLORS } from '../../util/Constants'
import Tabs from '../../components/Tabs'
import { IconButton, PrimaryButton, SecondaryButton } from '../../components/Button'
import { roundAssetAmount, shortAddress } from '../../util/Util'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import { getMethod } from '../../util/Managed'
import { Chevron } from '../../icons/Chevron'
import TimeText from '../../components/TimeText'
import LinkExternal from '../../components/LinkExternal'
import { ExternalLinkText } from '../Events'
import { genSectionItems } from '../../util/SectionItems'
import { Section, SectionContent, SectionItem, SubTitle } from '../../components/Shared'
import CreateDepositAddresCustomerModal from './CreateDepositAddresCustomerModal'
import CopyButton from '../../components/CopyButton'
import Spinner from '../../components/Spinner'
import Close from '../../icons/Close'
import Gate from '../../components/Gate'

interface CustomerDepositAccountsProps {
  depositAccounts?: DepositAccount[]
  isLoading: boolean
  fetchDepositAccounts: () => void
  customerId: string
}

const CustomerDepositAccounts = ({
  depositAccounts,
  isLoading,
  customerId,
  fetchDepositAccounts
}: CustomerDepositAccountsProps): JSX.Element => {
  const [selectedTab, setSelectedTab] = useState<string>('Details')
  const [selectedAccount, setSelectedAccount] = useState<DepositAccount | null>(null)
  const [showDetailsPanel, setShowDetailsPanel] = useState<boolean>(false)
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [isLoadingDeposits, setIsLoadingDeposits] = useState(false)
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] = useState(false)

  const handleModalOpen = (): void => {
    setIsCreateAccountModalOpen(true)
  }
  const handleModalClose = (): void => {
    setIsCreateAccountModalOpen(false)
  }

  const fetchDeposits = async (id: string): Promise<void> => {
    setIsLoadingDeposits(true)

    Radom.listDepositsByAccountId(id)
      .then(res => {
        setDeposits(res)
      })
      .finally(() => setIsLoadingDeposits(false))
  }

  if (!depositAccounts) {
    return <div>Loading...</div>
  }

  if (isLoading && depositAccounts.length === 0) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
      <Spinner />
    </div>
  }

  if (depositAccounts.length === 0) {
    return (
      <>
        <div style={{
          padding: 40,
          textAlign: 'center',
          fontSize: 14,
          color: RADOM_COLORS.GRAY4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14
        }}>
          <span>No deposit account for this contact yet.</span>
          <Gate permission='WriteDepositAccount'>
            <SecondaryButton onClick={() => handleModalOpen()}>Add deposit account</SecondaryButton>
          </Gate>
        </div>

        <CreateDepositAddresCustomerModal
          customerId={customerId}
          title='Create deposit address'
          visible={isCreateAccountModalOpen}
          onClose={handleModalClose}
          onAccountCreated={() => {
            handleModalClose()
            fetchDepositAccounts()
          }} />
      </>
    )
  }

  return (
    <>
      <Gate permission='WriteDepositAccount'>
        <PrimaryButton style={{ width: 180, alignSelf: 'end' }} onClick={handleModalOpen}>Create address</PrimaryButton>
      </Gate>
      <Section>
        <SubTitle style={{ fontSize: 18 }}>
                  Crypto deposit addresses
        </SubTitle>
        {
          depositAccounts.map((account, idx) => (
            <Section
              key={account.id}
              style={{
                backgroundColor: 'var(--surface, #ffffff)',
                cursor: 'pointer'
              }}
              onClick={() => {
                setSelectedTab('Details')
                setSelectedAccount(account)
                setShowDetailsPanel(true)
                if (deposits.length === 0 ||
                  deposits[0].depositAccountId !== account.id) {
                  fetchDeposits(account.id)
                }
              }}
            >
              <SectionContent numColumns={3}>
                {genSectionItems([
                  { heading: 'Address name', content: account.name },
                  { heading: 'Deposit Address', content: account.accountDetails.Crypto?.address },
                  <SectionItem key={idx}>
                    <SubTitle>Supported Assets</SubTitle>
                    {account.accountDetails.Crypto && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {account.accountDetails.Crypto.depositMethods.map(m => (
                          <PaymentMethodDisplay
                            key={m.network + (m.token ?? '')}
                            method={{ network: m.network, token: m.token }}
                            excludeName
                            showNetworkBadge
                            disableTooltip={false}
                          />
                        ))}
                      </div>
                    )}
                  </SectionItem>
                ], 3)}
              </SectionContent>
            </Section>
          ))
        }
      </Section>
      {showDetailsPanel && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(17, 17, 17, 0.28)',
            zIndex: 99
          }}
          onClick={() => setShowDetailsPanel(false)}
        />
      )}

      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 'min(100%, 560px)',
          height: '100%',
          background: 'var(--surface, #ffffff)',
          boxSizing: 'border-box',
          boxShadow: '-18px 0 48px rgba(15, 23, 42, 0.16)',
          transform: showDetailsPanel ? 'none' : 'translateX(150%)',
          transition: 'transform 0.3s ease',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          overflow: 'hidden',
          borderLeft: '1px solid var(--border-soft, #eceff3)'
        }}
      >
        <div
          style={{
            fontSize: 14,
            width: '100%',
            height: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: 24,
            gap: 16,
            overflowY: 'auto'
          }}
        >
          <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 12.5 }}>Deposit address</span>
              <span style={{ fontSize: 20, fontWeight: 500, color: RADOM_COLORS.BLACK, wordBreak: 'break-word' }}>{selectedAccount?.name}</span>
            </div>
            <IconButton type='button' onClick={() => setShowDetailsPanel(false)} style={{ padding: 6 }}>
              <Close width={16} height={16} />
            </IconButton>
          </div>

          <div
            style={{
              marginTop: 4,
              marginBottom: 8
            }}
          >
            <Tabs
              selectedTab={selectedTab}
              tabs={['Details', 'Deposits']}
              onTabChange={(tab) => { setSelectedTab(tab) }} />
          </div>

          {selectedTab === 'Details'
            ? <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Deposit address ID</span>
                <div style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '11px 12px',
                  borderRadius: 12,
                  border: '1px solid var(--border-soft, #eceff3)',
                  background: 'var(--surface-subtle, #f9fafb)'
                }}>
                  <span style={{ color: RADOM_COLORS.BLACK, fontFamily: 'var(--font-mono)', fontSize: 13, wordBreak: 'break-all' }}>
                    {selectedAccount?.id}
                  </span>
                  <CopyButton text={selectedAccount?.id ?? ''} />
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Deposit address</span>
                <div style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '11px 12px',
                  borderRadius: 12,
                  border: '1px solid var(--border-soft, #eceff3)',
                  background: 'var(--surface-subtle, #f9fafb)'
                }}>
                  <span style={{ color: RADOM_COLORS.BLACK, fontFamily: 'var(--font-mono)', fontSize: 13, wordBreak: 'break-all' }}>
                    {selectedAccount?.accountDetails.Crypto?.address}
                  </span>
                  <CopyButton text={selectedAccount?.accountDetails.Crypto?.address ?? ''} />
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Supported Assets</span>
                {selectedAccount?.accountDetails.Crypto && <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {
                    selectedAccount.accountDetails.Crypto?.depositMethods.map(m =>
                      <PaymentMethodDisplay
                        key={m.network + (m.token ?? '')}
                        method={{ network: m.network, token: m.token }}
                        excludeName
                        showNetworkBadge
                        disableTooltip={false} />
                    )
                  }
                </div>}
              </div>
            </>
            : <>
              <Table
                borderless
                disableOnClickState
                headers={['Deposit ID', 'Summary', 'Date'].map(h => <span key={h}>{h}</span>)}
                isLoading={isLoadingDeposits}
                items={deposits.map(d => {
                  let summary: {
                    in: {
                      img: string
                      amount: number | string
                      ticker: string
                    }
                    out?: {
                      img: string
                      amount: number | string
                      ticker: string
                    }
                  }

                  if (d.depositMethod.Crypto) {
                    const method = getMethod(d.depositMethod.Crypto.network, d.depositMethod.Crypto.token)
                    summary = {
                      in: {
                        img: method.logo,
                        amount: d.netAmount,
                        ticker: method.ticker
                      }
                    }
                  } else {
                    const outMethod = getMethod(d.depositMethod.Fiat?.onrampNetwork || '', d.depositMethod.Fiat?.onrampToken)
                    const inMethod = getMethod('Fiat', d.depositMethod.Fiat?.fiatCurrency)

                    summary = {
                      in: {
                        img: inMethod.logo,
                        amount: d.fiatGrossAmount || '',
                        ticker: inMethod.ticker
                      },
                      out: {
                        img: outMethod.logo,
                        amount: d.netAmount,
                        ticker: outMethod.ticker
                      }
                    }
                  }
                  return [
                    <div key={'1'}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ExternalLinkText
                          onClick={e => e.stopPropagation()}
                          target="_blank"
                          href={`/deposit/${d.id}?source=transactions`}>
                          {shortAddress(d.id, 3)}
                          <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
                        </ExternalLinkText>
                        <CopyButton text={d.id} />
                      </div>
                    </div>,
                    <div key={'2'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <img src={summary.in.img} style={{ width: 20, height: 20 }} />
                      <span>
                        {roundAssetAmount(summary.in.ticker, Number(summary.in.amount))}
                      </span>
                      <span>{summary.in.ticker}</span>
                      {
                        summary.out && <>
                          <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                          <img src={summary.out.img} style={{ width: 20, height: 20 }} />
                          <span>
                            {roundAssetAmount(summary.out.ticker, Number(summary.out.amount))}
                          </span>
                          <span>{summary.out.ticker}</span>
                        </>
                      }
                    </div>,
                    <div key={'3'}>
                      <TimeText date={new Date(d.depositedAt)} />
                    </div>
                  ]
                })}
                emptyPlaceholderText='No deposits yet'
              />
            </>}
        </div>
      </div>
      {

        <CreateDepositAddresCustomerModal
          customerId={customerId}
          title='Create deposit address'
          visible={isCreateAccountModalOpen}
          onClose={handleModalClose}
          onAccountCreated={() => {
            handleModalClose()
            fetchDepositAccounts()
          }} />
      }
    </>
  )
}

export default CustomerDepositAccounts
